import { inject, Injectable, signal } from '@angular/core';
import { RestApiV2Service } from '../http/rest-api-v2.service';
import { UserService } from '../user-management/user.service';
import { MessageService } from 'primeng/api';
import { Pacient } from '../../../shared/models/users/pacient.model';
import { ScreeningCycle } from '../../../shared/models/self-takers/self-taker.model';
import { ScreenerCenter } from '../../../shared/models/users/ScreenerCenter.model';
import { take } from 'rxjs';
import { HealthSecretaryUserInfo } from '../../../shared/models/users/user.model';
import { PopulationType, SelfTakerResult, TriageResult } from './self-takers.service';
import { DashboardColorsConfig } from '../../../features/self-takers-module/health-secretary/config/dashboard-colors.config';
import { PopulationDistribution, NaatVphResult } from '../../../features/self-takers-module/health-secretary/config/health-secretary-charts.config';
import { AgeDistribution, buildAgeBracketStats } from '../../../features/self-takers-module/shared/self-taker-dashboard-age.util';
import { OpportunityMetricsData, calculateOpportunityMetrics } from '../../../features/self-takers-module/shared/self-taker-opportunity.util';

export interface DashboardKPI {
  title: string;
  value: string | number;
}

export interface PositivityRateData {
  labels: string[];
  series: Array<{
    name: string;
    data: number[];
    color: string;
  }>;
  chart: NaatVphResult[];
}

export interface PopulationDistributionData {
  labels: string[];
  series: number[];
  colors: string[];
  chart: PopulationDistribution;
}

export interface SelfTakerDashboardStats {
  kpis: DashboardKPI[];
  positivityRateOverTime: PositivityRateData;
  populationDistribution: PopulationDistributionData;
  ageDistribution: AgeDistribution;
  opportunityMetrics: OpportunityMetricsData;
}

@Injectable({
  providedIn: 'root',
})
export class HealthSecretaryService {
  private readonly restApi = inject(RestApiV2Service);
  private userService = inject(UserService);
  private messageService = inject(MessageService);

  readonly data = signal
  <{ 
    my_pacients: {pacient: Pacient, screening_cycles: ScreeningCycle[]}[], 
    screener_centers: {screener_center: ScreenerCenter, pacients: {pacient: Pacient, screening_cycles: ScreeningCycle[]}[]}[]
  }>({ my_pacients: [], screener_centers: [] });


  getMyData() {
    const user = this.userService.userData()?.user_info as HealthSecretaryUserInfo;
    const response = this.restApi.get<{ data: { my_pacients: any[], screener_centers: any[] } }>('health-secretary/get-my-data', { user_id: user!.id });

    response.request$
    .pipe(take(1))
    .subscribe(response => {
      console.log(response.data);
      this.data.set(response.data);
    });

    response.error$
    .subscribe(error => {
      if(!error) return;

      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error al obtener los datos' });
    });
  }

  processData(data?: {pacient: Pacient, screening_cycles: ScreeningCycle[]}[]): SelfTakerDashboardStats {
    // Aplanar el array de arrays en un único array de ScreeningCycle
    const allScreeningCycles = data?.flatMap(item => item.screening_cycles) || [];

    const stats: SelfTakerDashboardStats = {
      kpis: this.calculateKPIs(allScreeningCycles),
      positivityRateOverTime: this.calculatePositivityRateOverTime(allScreeningCycles),
      populationDistribution: this.calculatePopulationDistribution(allScreeningCycles),
      ageDistribution: buildAgeBracketStats(allScreeningCycles, 'by_cycle'),
      opportunityMetrics: calculateOpportunityMetrics(allScreeningCycles),
    };

    return stats;
  }

  private calculateKPIs(screeningCycles: ScreeningCycle[]): DashboardKPI[] {
    const totalSelfTakers = screeningCycles.length;
    const activePacients = new Set(screeningCycles.map(cycle => cycle.pacient_id)).size;
    const positiveResults = screeningCycles.filter(cycle => 
      cycle.self_taker?.result_id === SelfTakerResult.POSITIVE
    ).length;
    const completedTriajes = screeningCycles.filter(cycle => 
      cycle.self_taker_triage?.result_id === TriageResult.POSITIVE ||
      cycle.self_taker_triage?.result_id === TriageResult.NEGATIVE
    ).length;

    const completedCitologies = screeningCycles.filter(cycle =>
      Boolean(cycle.self_taker_citology?.result_received_at)
    ).length;

    const usesCitologyFlow = screeningCycles.some((c) => c.screening_flow === 'citology');

    const positivityRate = totalSelfTakers > 0 ? (positiveResults / totalSelfTakers) * 100 : 0;

    return [
      {
        title: 'Pacientes',
        value: activePacients.toLocaleString()
      },
      {
        title: 'Auto tomas',
        value: totalSelfTakers.toLocaleString(),
      },
      {
        title: 'Tasa de positividad',
        value: `${positivityRate.toFixed(1)}%`,
      },
      {
        title: usesCitologyFlow ? 'Citologías completadas' : 'Triajes completados',
        value: usesCitologyFlow ? completedCitologies.toLocaleString() : completedTriajes.toLocaleString(),
      }
    ];
  }

  private calculatePositivityRateOverTime(screeningCycles: ScreeningCycle[]): PositivityRateData {
    // Agrupar por mes y calcular positivos/negativos
    const monthlyData = this.groupByMonth(screeningCycles);

    const labels = Object.keys(monthlyData).sort();
    const positiveData = labels.map(month => monthlyData[month].positive);
    const negativeData = labels.map(month => monthlyData[month].negative);
    const chart = labels.map(month => ({
      date: month,
      positive: monthlyData[month].positive,
      negative: monthlyData[month].negative,
    }));

    if (labels.length <= 1) {
      labels.unshift('Base');
      positiveData.unshift(0);
      negativeData.unshift(0);
      chart.unshift({
        date: 'Base',
        positive: 0,
        negative: 0,
      });
    }

    return {
      labels,
      series: [
        { name: 'Pruebas positivas', data: positiveData, color: DashboardColorsConfig.naatVph.positive },
        { name: 'Pruebas negativas', data: negativeData, color: DashboardColorsConfig.naatVph.negative }
      ],
      chart,
    };
  }

  private calculatePopulationDistribution(screeningCycles: ScreeningCycle[]): PopulationDistributionData {
    const populationCounts = screeningCycles.reduce((acc, cycle) => {
      const populationId = cycle.pacient?.population?.id != null ? Number(cycle.pacient.population.id) : PopulationType.GENERAL;
      const populationName = populationId === PopulationType.VIH ? 'VIH' : 'General';
      acc[populationName] = (acc[populationName] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const labels = Object.keys(populationCounts);
    const series = Object.values(populationCounts) as number[];
    let generalTotal = 0;
    let vihTotal = 0;

    const colors = labels.map(label => {
      const normalized = label.trim().toLowerCase();
      const isVih = normalized.includes('vih');

      if (isVih) {
        vihTotal += populationCounts[label];
        return DashboardColorsConfig.population.vih;
      }

      generalTotal += populationCounts[label];
      return DashboardColorsConfig.population.general;
    });

    const chart: PopulationDistribution = {
      general: generalTotal,
      vih: vihTotal,
    };

    return { labels, series, colors, chart };
  }

  private groupByMonth(screeningCycles: ScreeningCycle[]): Record<string, { positive: number; negative: number }> {
    return screeningCycles.reduce((acc, cycle) => {
      const date = new Date(cycle.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      if (!acc[monthKey]) {
        acc[monthKey] = { positive: 0, negative: 0 };
      }

      if (cycle.self_taker?.result_id === SelfTakerResult.POSITIVE) {
        acc[monthKey].positive++;
      } else if (cycle.self_taker?.result_id === SelfTakerResult.NEGATIVE) {
        acc[monthKey].negative++;
      }

      return acc;
    }, {} as Record<string, { positive: number; negative: number }>);
  }

  manageHealthSecretaryData(cycle: ScreeningCycle) {
    this.data.update(currentData => {
      // Función helper para actualizar o agregar ciclo en un array de pacientes
      const updateOrAddCycle = (pacientsArray: {pacient: Pacient, screening_cycles: ScreeningCycle[]}[]) => {
        return pacientsArray.map(item => {
          // Buscar si el paciente tiene este ciclo
          const cycleIndex = item.screening_cycles.findIndex(c => c.id === cycle.id);
          
          if (cycleIndex !== -1) {
            // Actualizar el ciclo existente
            const updatedCycles = [...item.screening_cycles];
            updatedCycles[cycleIndex] = cycle;
            return { ...item, screening_cycles: updatedCycles };
          } else if (String(item.pacient.id) === String(cycle.pacient_id)) {
            // El ciclo pertenece a este paciente pero no existe, agregarlo
            return { ...item, screening_cycles: [...item.screening_cycles, cycle] };
          }
          return item;
        });
      };

      // Actualizar my_pacients
      const updatedMyPacients = updateOrAddCycle(currentData.my_pacients);
      
      // Verificar si el paciente existe en my_pacients, si no, agregarlo
      const pacientExistsInMyPacients = updatedMyPacients.some(item => String(item.pacient.id) === String(cycle.pacient_id));
      if (!pacientExistsInMyPacients && cycle.pacient) {
        updatedMyPacients.push({
          pacient: cycle.pacient,
          screening_cycles: [cycle]
        });
      }

      // Actualizar screener_centers
      const updatedScreenerCenters = currentData.screener_centers.map(screenerCenterItem => {
        // Buscar si algún paciente de este screener center tiene el ciclo
        const pacientsArray = screenerCenterItem.pacients;
        const pacientIndex = pacientsArray.findIndex(p => 
          String(p.pacient.id) === String(cycle.pacient_id)
        );
        
        if (pacientIndex !== -1) {
          // El paciente existe en este screener center, actualizar o agregar el ciclo
          const updatedPacients = pacientsArray.map((pacientItem, idx) => {
            if (idx === pacientIndex) {
              const cycleIndex = pacientItem.screening_cycles.findIndex((c: ScreeningCycle) => c.id === cycle.id);
              
              if (cycleIndex !== -1) {
                // Actualizar el ciclo existente
                const updatedCycles = [...pacientItem.screening_cycles];
                updatedCycles[cycleIndex] = cycle;
                return { ...pacientItem, screening_cycles: updatedCycles };
              } else {
                // Agregar el nuevo ciclo
                return { ...pacientItem, screening_cycles: [...pacientItem.screening_cycles, cycle] };
              }
            }
            return pacientItem;
          });
          
          return { ...screenerCenterItem, pacients: updatedPacients };
        } else if (cycle.pacient) {
          // El paciente no existe en este screener center, pero el ciclo tiene información del paciente
          // Agregar el paciente con el ciclo
          return {
            ...screenerCenterItem,
            pacients: [...screenerCenterItem.pacients, {
              pacient: cycle.pacient,
              screening_cycles: [cycle]
            }]
          };
        }
        
        return screenerCenterItem;
      });

      return {
        my_pacients: updatedMyPacients,
        screener_centers: updatedScreenerCenters
      };
    });
  }
}
